/**
 * Connection keys and cross-axis counting (WQ-343).
 *
 * Connections are stored as mirrored pairs (child + parent, or peer + peer), so
 * one link appears in both nodes' `edges`. `connectionKey` maps both rows of a
 * pair to the same key; ConnectionLines and the Cross-Axis heatmap share it so
 * each link is drawn and counted once.
 */

import type { AdjacencyAxis, AdjacencyNode, ConnectionEdge } from '../types/adjacency';

/** Unordered key for a node pair. */
export function edgeKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/** One key per connection; parent-child and peer links between the same pair stay distinct. */
export function connectionKey(nodeId: string, edge: Pick<ConnectionEdge, 'targetId' | 'relationship'>): string {
  const isPC = edge.relationship === 'parent' || edge.relationship === 'child';
  return isPC ? `${edgeKey(nodeId, edge.targetId)}:pc` : edgeKey(nodeId, edge.targetId);
}

/**
 * Connections between axes, each link counted once. A link between axes A and B
 * adds 1 to both [A][B] and [B][A]; a same-axis link adds 1 to [A][A]. Links to
 * Core nodes, or to nodes outside `nodes`, are not counted.
 */
export function countAxisConnections(
  nodes: AdjacencyNode[],
  axes: readonly AdjacencyAxis[],
): Map<AdjacencyAxis, Map<AdjacencyAxis, number>> {
  const mat = new Map<AdjacencyAxis, Map<AdjacencyAxis, number>>();
  for (const a of axes) mat.set(a, new Map(axes.map((b) => [b, 0])));

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const seen = new Set<string>();
  const bump = (from: AdjacencyAxis, to: AdjacencyAxis) => {
    const row = mat.get(from);
    if (row && row.has(to)) row.set(to, (row.get(to) ?? 0) + 1);
  };

  for (const n of nodes) {
    if (n.adjacencyAxis === 'Core') continue;
    for (const edge of n.edges) {
      const target = nodeMap.get(edge.targetId);
      if (!target || target.adjacencyAxis === 'Core') continue;
      const key = connectionKey(n.id, edge);
      if (seen.has(key)) continue;
      seen.add(key);
      bump(n.adjacencyAxis, target.adjacencyAxis);
      if (target.adjacencyAxis !== n.adjacencyAxis) bump(target.adjacencyAxis, n.adjacencyAxis);
    }
  }
  return mat;
}
